import React, { useRef } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Editor } from "@tinymce/tinymce-react";
import { GripVertical, Trash2, Image as ImageIcon, Youtube, Quote, ListChecks, Music, FileText, FileDown, Plus, MoveUp, MoveDown, ArrowUpDown } from "lucide-react";
import DropZone from "./DropZone";

/**
 * InsightBlocksEditor
 * ------------------------------------------------------------------
 * Dedicated editor for Insight article body blocks. Distinct from
 * ContentBlocksEditor (which powers Cases). Supported blocks:
 *   - richtext     { html, spacing_px, position }
 *   - youtube      { url, caption, spacing_px, position }
 *   - image        { src, alt, caption, spacing_px, position }
 *   - colored_list { items:string[], color_key, spacing_px, position }
 *   - quote        { text, author, spacing_px, position }
 *   - audio        { src, title, spacing_px, position }
 *   - pdf          { src, thumb, title, caption, spacing_px, position }
 *
 * `position` = "before" | "after" | "both"
 *   - "before": renderiza ANTES do corpo do artigo
 *   - "after":  renderiza DEPOIS do corpo do artigo (default legado)
 *   - "both":   renderiza NOS DOIS lugares (útil pra PDFs de download —
 *              maximiza a chance do usuário clicar sem rolar até o fim)
 * Default para NOVOS blocos PDF é "before"; demais tipos default "after".
 */

let _iid = 0;
export const newInsightBlockId = () => `ib_${Date.now()}_${++_iid}`;

// Fixed Places brand palette for colored_list blocks
export const INSIGHT_PALETTE = [
  { key: "yellow", label: "Amarelo Places", bg: "#D4FF00", text: "#000000" },
  { key: "orange", label: "Laranja Places", bg: "#FF5E00", text: "#FFFFFF" },
  { key: "black",  label: "Preto",          bg: "#000000", text: "#FFFFFF" },
  { key: "dark",   label: "Grafite",        bg: "#171717", text: "#FFFFFF" },
  { key: "cream",  label: "Creme",          bg: "#FFF5E1", text: "#000000" },
  { key: "soft",   label: "Neutro claro",   bg: "#F3F1EE", text: "#000000" },
];

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

const TYPE_META = {
  richtext:     { label: "Texto formatado", Icon: FileText },
  youtube:      { label: "Vídeo YouTube",   Icon: Youtube },
  image:        { label: "Imagem",          Icon: ImageIcon },
  colored_list: { label: "Lista colorida",  Icon: ListChecks },
  quote:        { label: "Citação grande",  Icon: Quote },
  audio:        { label: "Áudio",           Icon: Music },
  pdf:          { label: "PDF (thumb + título)", Icon: FileDown },
};

function SortableInsightBlock({ block, index, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const upd = (patch) => onChange({ ...block, ...patch });
  const meta = TYPE_META[block.type] || {};
  const Icon = meta.Icon || FileText;

  return (
    <div ref={setNodeRef} style={style} className="border border-black/10 rounded-md bg-white overflow-hidden" data-testid={`iblock-${block.type}-${index}`}>
      <header className="flex items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-black/10 flex-wrap">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-black" title="Arrastar para reordenar">
          <GripVertical className="w-4 h-4" />
        </button>
        <Icon className="w-4 h-4 text-accent-orange" />
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{meta.label || block.type}</span>
        <span className="text-[10px] font-mono text-neutral-400">#{index + 1}</span>
        {/* PDF blocks: warn only when placed STRICTLY after — "both" and
            "before" are considered correct placements (PDF visível no topo). */}
        {block.type === "pdf" && (block.position || "after") === "after" && (
          <span className="text-[10px] uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-bold" title="Este PDF vai aparecer só no FIM do post. Se você quer que apareça no topo (ou em ambos os lugares), mude no toggle.">
            ⚠ só no fim do post
          </span>
        )}
        {/* Position toggle — 3 opções: antes, ambos, depois. */}
        <div className="flex items-center gap-1 ml-2 border border-black/10 rounded-full p-0.5" data-testid={`iblock-position-${index}`}>
          <button
            type="button"
            onClick={() => upd({ position: "before" })}
            data-testid={`iblock-position-before-${index}`}
            title="Renderizar antes do texto do post"
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1 transition ${(block.position || "after") === "before" ? "bg-accent-orange text-white shadow" : "text-neutral-500 hover:bg-black/5"}`}>
            <MoveUp className="w-3 h-3" /> Antes
          </button>
          <button
            type="button"
            onClick={() => upd({ position: "both" })}
            data-testid={`iblock-position-both-${index}`}
            title="Renderizar ANTES E DEPOIS do texto (dois cards)"
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1 transition ${(block.position || "after") === "both" ? "bg-accent-yellow text-black shadow" : "text-neutral-500 hover:bg-black/5"}`}>
            <ArrowUpDown className="w-3 h-3" /> Antes e depois
          </button>
          <button
            type="button"
            onClick={() => upd({ position: "after" })}
            data-testid={`iblock-position-after-${index}`}
            title="Renderizar depois do texto do post"
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1 transition ${(block.position || "after") === "after" ? "bg-black text-white shadow" : "text-neutral-500 hover:bg-black/5"}`}>
            <MoveDown className="w-3 h-3" /> Depois
          </button>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="text-[10px] text-neutral-500 flex items-center gap-1">
            Espaço abaixo:
            <input type="range" min={0} max={200} step={4} value={block.spacing_px ?? 24}
              onChange={(e) => upd({ spacing_px: Number(e.target.value) })}
              data-testid={`iblock-spacing-${index}`} className="w-24" />
            <span className="font-mono w-10">{block.spacing_px ?? 24}px</span>
          </label>
          <button onClick={() => onRemove(block.id)} className="text-red-500 hover:text-red-700" title="Remover">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>
      <div className="p-3">
        {block.type === "richtext"     && <RichTextEditor block={block} upd={upd} index={index} />}
        {block.type === "youtube"      && <YoutubeEditor block={block} upd={upd} index={index} />}
        {block.type === "image"        && <ImageEditor block={block} upd={upd} index={index} />}
        {block.type === "colored_list" && <ColoredListEditor block={block} upd={upd} index={index} />}
        {block.type === "quote"        && <QuoteEditor block={block} upd={upd} index={index} />}
        {block.type === "audio"        && <AudioEditor block={block} upd={upd} index={index} />}
        {block.type === "pdf"          && <PdfEditor block={block} upd={upd} index={index} />}
      </div>
    </div>
  );
}

/* ============ Rich text ============ */
function RichTextEditor({ block, upd, index }) {
  const ref = useRef(null);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Conteúdo</div>
      <Editor
        tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@7/tinymce.min.js"
        onInit={(_e, editor) => (ref.current = editor)}
        initialValue={block.html || "<p></p>"}
        onEditorChange={(html) => upd({ html })}
        init={{
          height: 260, menubar: false,
          plugins: "lists link autolink code quickbars wordcount",
          toolbar: "undo redo | h2 h3 | bold italic underline | bullist numlist | link removeformat | code",
          content_style: "body{font-family:Poppins,sans-serif;font-size:15px;line-height:1.7;}",
          license_key: "gpl",
        }}
      />
      <div className="text-[10px] text-neutral-400 mt-1" data-testid={`iblock-richtext-hint-${index}`}>
        Use este bloco para parágrafos formatados entre imagens, vídeos e listas.
      </div>
    </div>
  );
}

/* ============ YouTube ============ */
function YoutubeEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-neutral-500">URL do YouTube</label>
        <input value={block.url || ""} onChange={(e) => upd({ url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..." data-testid={`iblock-youtube-url-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
      </div>
      <div>
        <label className="text-xs text-neutral-500">Legenda (opcional)</label>
        <input value={block.caption || ""} onChange={(e) => upd({ caption: e.target.value })}
          data-testid={`iblock-youtube-caption-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

/* ============ Image ============ */
function ImageEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      {block.src && (
        <div className="max-w-md">
          <img src={resolveSrc(block.src)} alt="" className="w-full rounded border border-black/10" />
        </div>
      )}
      <input value={block.src || ""} onChange={(e) => upd({ src: e.target.value })}
        placeholder="URL da imagem ou caminho /api/media/..." data-testid={`iblock-image-src-${index}`}
        className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
      <DropZone multiple={false} accept={{ "image/*": [] }} hint="PNG/JPG/WEBP · até 25MB"
        onUploaded={(f) => upd({ src: f.url })} />
      <div>
        <label className="text-xs text-neutral-500">Alt (acessibilidade + SEO)</label>
        <input value={block.alt || ""} onChange={(e) => upd({ alt: e.target.value })}
          data-testid={`iblock-image-alt-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
      <div>
        <label className="text-xs text-neutral-500">Legenda (opcional)</label>
        <input value={block.caption || ""} onChange={(e) => upd({ caption: e.target.value })}
          data-testid={`iblock-image-caption-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

/* ============ Colored list ============ */
function ColoredListEditor({ block, upd, index }) {
  const items = Array.isArray(block.items) ? block.items : [];
  const setItem = (i, v) => upd({ items: items.map((it, j) => j === i ? v : it) });
  const remove = (i) => upd({ items: items.filter((_, j) => j !== i) });
  const add = () => upd({ items: [...items, ""] });
  const currentColor = INSIGHT_PALETTE.find((c) => c.key === (block.color_key || "yellow")) || INSIGHT_PALETTE[0];

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Cor da caixa</div>
        <div className="flex flex-wrap gap-2">
          {INSIGHT_PALETTE.map((c) => (
            <button key={c.key} type="button" onClick={() => upd({ color_key: c.key })}
              data-testid={`iblock-color-${c.key}-${index}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${block.color_key === c.key ? "ring-2 ring-black scale-105" : "border-black/15 hover:scale-105"}`}
              style={{ background: c.bg, color: c.text }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Título da caixa (opcional)</div>
        <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
          data-testid={`iblock-list-title-${index}`} placeholder="Ex: Principais desafios"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Itens da lista</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs font-mono text-neutral-400 w-6">{i + 1}.</span>
              <input value={it} onChange={(e) => setItem(i, e.target.value)}
                data-testid={`iblock-list-item-${index}-${i}`}
                className="flex-1 border border-black/15 px-3 py-2 rounded text-sm" />
              <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700" title="Remover">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button onClick={add} data-testid={`iblock-list-add-${index}`}
            className="text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-accent-orange">
            <Plus className="w-3 h-3 inline mr-1" /> Adicionar item
          </button>
        </div>
      </div>
      {/* Preview */}
      <div className="mt-3 p-4 rounded-md" style={{ background: currentColor.bg, color: currentColor.text }}>
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Prévia</div>
        {block.title && <div className="font-display text-2xl mb-3">{block.title}</div>}
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2"><span className="opacity-70">•</span><span>{it || <em className="opacity-50">(vazio)</em>}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ============ Quote ============ */
function QuoteEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-neutral-500">Citação</label>
        <textarea value={block.text || ""} onChange={(e) => upd({ text: e.target.value })} rows={4}
          data-testid={`iblock-quote-text-${index}`}
          placeholder="Uma frase marcante em destaque, tamanho grande."
          className="w-full border border-black/15 px-3 py-2 rounded text-lg font-display leading-tight" />
      </div>
      <div>
        <label className="text-xs text-neutral-500">Autor / Fonte (opcional)</label>
        <input value={block.author || ""} onChange={(e) => upd({ author: e.target.value })}
          data-testid={`iblock-quote-author-${index}`}
          placeholder="— Nome, cargo"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

/* ============ Audio ============ */
function AudioEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      {block.src && (
        <audio controls src={resolveSrc(block.src)} className="w-full" data-testid={`iblock-audio-preview-${index}`} />
      )}
      <input value={block.src || ""} onChange={(e) => upd({ src: e.target.value })}
        placeholder="URL do áudio ou /api/media/..." data-testid={`iblock-audio-src-${index}`}
        className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
      <DropZone multiple={false} accept={{ "audio/*": [".mp3", ".m4a", ".wav", ".ogg"] }}
        hint="MP3/M4A/WAV/OGG · até 100MB"
        onUploaded={(f) => upd({ src: f.url })} />
      <div>
        <label className="text-xs text-neutral-500">Título / descrição (opcional)</label>
        <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
          data-testid={`iblock-audio-title-${index}`}
          placeholder="Ex: Entrevista com o Head de Estratégia"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

/* ============ PDF ============ */
function PdfEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-neutral-500">Título (aparece ao lado do thumb)</label>
        <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
          data-testid={`iblock-pdf-title-${index}`}
          placeholder="Ex: Whitepaper — Mercado imobiliário 2026"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>

      <div>
        <label className="text-xs text-neutral-500">Arquivo PDF</label>
        {block.src && (
          <div className="mt-1 mb-2 flex items-center gap-2 text-xs text-green-700">
            <FileDown className="w-3.5 h-3.5" />
            <a href={resolveSrc(block.src)} target="_blank" rel="noopener noreferrer" className="underline break-all">{block.src}</a>
          </div>
        )}
        <input value={block.src || ""} onChange={(e) => upd({ src: e.target.value })}
          placeholder="URL do PDF ou /api/media/..." data-testid={`iblock-pdf-src-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
        <div className="mt-2">
          <DropZone multiple={false} accept={{ "application/pdf": [".pdf"] }}
            hint="PDF · até 25MB"
            onUploaded={(f) => upd({ src: f.url })} />
        </div>
      </div>

      <div>
        <label className="text-xs text-neutral-500">Thumbnail (imagem) — opcional</label>
        {block.thumb && (
          <div className="mt-1 mb-2 max-w-[140px]">
            <img src={resolveSrc(block.thumb)} alt="" className="w-full aspect-[3/4] object-cover rounded border border-black/10" />
          </div>
        )}
        <input value={block.thumb || ""} onChange={(e) => upd({ thumb: e.target.value })}
          placeholder="URL da imagem thumb ou /api/media/..." data-testid={`iblock-pdf-thumb-${index}`}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
        <div className="mt-2">
          <DropZone multiple={false} accept={{ "image/*": [] }} hint="PNG/JPG/WEBP · até 25MB"
            onUploaded={(f) => upd({ thumb: f.url })} />
        </div>
        <div className="text-[10px] text-neutral-400 mt-1">Se vazio, mostramos um ícone de PDF padrão.</div>
      </div>

      <div>
        <label className="text-xs text-neutral-500">Legenda curta (opcional)</label>
        <input value={block.caption || ""} onChange={(e) => upd({ caption: e.target.value })}
          data-testid={`iblock-pdf-caption-${index}`}
          placeholder="Ex: 24 páginas · 3.2 MB · Grátis"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

/* ============ Main ============ */
export default function InsightBlocksEditor({ blocks = [], onChange, testidPrefix = "insight-blocks" }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = blocks.findIndex((b) => b.id === active.id);
    const newI = blocks.findIndex((b) => b.id === over.id);
    onChange(arrayMove(blocks, oldI, newI));
  };

  const updateBlock = (i, next) => onChange(blocks.map((b, j) => j === i ? next : b));
  const removeBlock = (id) => onChange(blocks.filter((b) => b.id !== id));

  const add = (type) => {
    // PDF blocks default to "before" because that's the standard placement for
    // lead-gate materials (usuários querem baixar antes de ler). All other
    // blocks default to "after" — mantém retro-compat com uso anterior.
    const defaultPosition = type === "pdf" ? "before" : "after";
    const base = { id: newInsightBlockId(), type, spacing_px: 24, position: defaultPosition };
    if (type === "richtext")     Object.assign(base, { html: "<p></p>" });
    if (type === "youtube")      Object.assign(base, { url: "", caption: "" });
    if (type === "image")        Object.assign(base, { src: "", alt: "", caption: "" });
    if (type === "colored_list") Object.assign(base, { color_key: "yellow", title: "", items: [""] });
    if (type === "quote")        Object.assign(base, { text: "", author: "" });
    if (type === "audio")        Object.assign(base, { src: "", title: "" });
    if (type === "pdf")          Object.assign(base, { src: "", thumb: "", title: "", caption: "" });
    onChange([...blocks, base]);
  };

  const BTN = "text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors";

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => add("richtext")}     data-testid={`${testidPrefix}-add-richtext`}     className={BTN}><FileText  className="w-3 h-3 inline mr-1" /> Texto</button>
        <button onClick={() => add("youtube")}      data-testid={`${testidPrefix}-add-youtube`}      className={BTN}><Youtube   className="w-3 h-3 inline mr-1" /> YouTube</button>
        <button onClick={() => add("image")}        data-testid={`${testidPrefix}-add-image`}        className={BTN}><ImageIcon className="w-3 h-3 inline mr-1" /> Imagem</button>
        <button onClick={() => add("colored_list")} data-testid={`${testidPrefix}-add-colored-list`} className={BTN}><ListChecks className="w-3 h-3 inline mr-1" /> Lista colorida</button>
        <button onClick={() => add("quote")}        data-testid={`${testidPrefix}-add-quote`}        className={BTN}><Quote     className="w-3 h-3 inline mr-1" /> Citação</button>
        <button onClick={() => add("audio")}        data-testid={`${testidPrefix}-add-audio`}        className={BTN}><Music     className="w-3 h-3 inline mr-1" /> Áudio</button>
        <button onClick={() => add("pdf")}          data-testid={`${testidPrefix}-add-pdf`}          className={BTN}><FileDown  className="w-3 h-3 inline mr-1" /> PDF</button>
      </div>

      {blocks.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-md p-10 text-center text-sm text-neutral-500">
          Nenhum bloco ainda. Escolha um tipo acima para enriquecer o artigo.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {blocks.map((block, i) => (
                <SortableInsightBlock key={block.id} block={block} index={i}
                  onChange={(next) => updateBlock(i, next)} onRemove={removeBlock} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
